/**
 * API service for BIM Comply backend communication
 */

import { ChatMessage } from 'types/types';
import { getApiHost, getAuthHeaders, API_ENDPOINTS } from 'constants/config';
import { serializeRequest } from 'utils/requestSerialization';

export interface ValidationRequest {
  Source: string;
  Validator: string;
  Country: string;
  Language: string;
  ElementType: string;
  Content: string;
  ExcludedDocuments?: string[];
  Tools?: string;
  /**
   * Opt-in validation debug capture (Debug Mode). Only ever sent when the
   * user has explicitly enabled Debug Mode; the API authorizes capture
   * narrowly server-side and fails closed for unauthorized callers.
   */
  DebugCapture?: boolean;
  /** Client-generated correlation id, only present alongside DebugCapture. */
  CorrelationId?: string;
}

export interface ChatRequest {
  Validator: string;
  Country: string;
  Language: string;
  Messages: ChatMessage[];
  Content: string;
}

export interface FeedbackRequest {
  Type: string;
  Content: string;
  Source: string;
  Validator: string;
  Country: string;
  Language: string;
  ElementType: string;
  Input: string;
  Output: string;
}

/**
 * Stream handler callback type
 */
export type StreamHandler = (chunk: string) => void;

/**
 * Submit validation request with streaming response
 */
export async function submitValidation(
  request: ValidationRequest,
  token: string | undefined,
  address: string | null | undefined,
  signal?: AbortSignal,
  onStream?: StreamHandler
): Promise<void> {
  const host = getApiHost(address);

  const response = await fetch(host + API_ENDPOINTS.VALIDATOR, {
    method: "POST",
    headers: getAuthHeaders(token),
    body: serializeRequest(request),
    signal
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('UNAUTHORIZED');
    }

    const reader = response.body?.getReader();
    if (reader) {
      const { value } = await reader.read();
      const convertedText = new TextDecoder().decode(value);
      throw new Error(JSON.parse(convertedText).detail);
    }
    throw new Error(`Request failed with status ${response.status}`);
  }

  // Stream processing
  const reader = response.body!.getReader();

  async function pump(): Promise<void> {
    if (signal?.aborted) {
      return;
    }

    const { done, value } = await reader.read();

    if (signal?.aborted) {
      return;
    }

    if (done) {
      return;
    }

    const convertedText = new TextDecoder().decode(value);
    onStream?.(convertedText);

    return pump();
  }

  await pump();
}

/**
 * Submit chat request with streaming response
 */
export async function submitChat(
  request: ChatRequest,
  token: string | undefined,
  address: string | null | undefined,
  signal?: AbortSignal,
  onStream?: StreamHandler
): Promise<void> {
  const host = getApiHost(address);

  const response = await fetch(host + API_ENDPOINTS.CHAT, {
    method: "POST",
    headers: getAuthHeaders(token),
    body: JSON.stringify(request),
    signal
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('UNAUTHORIZED');
    }

    const reader = response.body?.getReader();
    if (reader) {
      const { value } = await reader.read();
      const convertedText = new TextDecoder().decode(value);
      throw new Error(JSON.parse(convertedText).detail);
    }
    throw new Error(`Request failed with status ${response.status}`);
  }

  // Stream processing
  const reader = response.body!.getReader();

  async function pump(): Promise<void> {
    if (signal?.aborted) {
      return;
    }

    const { done, value } = await reader.read();

    if (signal?.aborted) {
      return;
    }

    if (done) {
      return;
    }

    const convertedText = new TextDecoder().decode(value);
    onStream?.(convertedText);

    return pump();
  }

  await pump();
}

/**
 * Submit user feedback
 */
export async function submitFeedback(
  request: FeedbackRequest,
  token: string | undefined,
  address: string | null | undefined
): Promise<void> {
  const host = getApiHost(address);

  const response = await fetch(host + API_ENDPOINTS.FEEDBACK, {
    method: "POST",
    headers: getAuthHeaders(token),
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    throw new Error(`Feedback submission failed with status ${response.status}`);
  }
}

/**
 * Get available countries for validation
 */
export async function getAvailableCountries(
  address: string | null | undefined
): Promise<string[]> {
  const host = getApiHost(address);

  const response = await fetch(host + API_ENDPOINTS.AVAILABLE_COUNTRIES, {
    method: "GET",
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const reader = response.body?.getReader();
    if (reader) {
      const { value } = await reader.read();
      const convertedText = new TextDecoder().decode(value);
      throw new Error(JSON.parse(convertedText).detail);
    }
    throw new Error(`Request failed with status ${response.status}`);
  }

  const reader = response.body!.getReader();
  const { value } = await reader.read();
  const countries = JSON.parse(new TextDecoder().decode(value));
  return countries;
}

/**
 * Legislation document interface
 */
export interface LegislationDocument {
  name: string;
  [key: string]: any;
}

/**
 * Get legislation documents for a country
 */
export async function getLegislationDocuments(
  country: string,
  token: string | undefined,
  address: string | null | undefined
): Promise<LegislationDocument[]> {
  const host = getApiHost(address);

  const response = await fetch(`${host}${API_ENDPOINTS.LEGISLATION}/${encodeURIComponent(country)}`, {
    method: "GET",
    headers: getAuthHeaders(token)
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('UNAUTHORIZED');
    }
    const reader = response.body?.getReader();
    if (reader) {
      const { value } = await reader.read();
      const convertedText = new TextDecoder().decode(value);
      throw new Error(JSON.parse(convertedText).detail);
    }
    throw new Error(`Request failed with status ${response.status}`);
  }

  const reader = response.body!.getReader();
  const { value } = await reader.read();
  const documents: string[] = JSON.parse(new TextDecoder().decode(value));
  // Transform string array to objects with name property
  return documents.map(name => ({ name }));
}

/**
 * Get element type based on category, family, and type
 */
export async function getElementType(
  category: string,
  family: string,
  type: string,
  token: string | undefined,
  host: string | null | undefined
): Promise<string> {
  const request = {
    category,
    family,
    type
  };

  const response = await fetch(`${host}${API_ENDPOINTS.ELEMENT_TYPE}`, {
    method: "POST",
    headers: getAuthHeaders(token),
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('UNAUTHORIZED');
    }
    const reader = response.body?.getReader();
    if (reader) {
      const { value } = await reader.read();
      const convertedText = new TextDecoder().decode(value);
      throw new Error(JSON.parse(convertedText).detail);
    }
    throw new Error(`Request failed with status ${response.status}`);
  }

  const reader = response.body!.getReader();
  const { value } = await reader.read();
  const elementType = new TextDecoder().decode(value);
  return elementType.replace(/"/g, ''); // Remove quotes if present
}
